import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage, parseStorageKey, getMediaUrl } from '@vibesites/ui-primitives';
import { contrastRatio, getContrastTextColor, relativeLuminance } from '../utils/luminance';
import { condenseOfficeHours, type OfficeHourEntry } from './PropertyInfoPanel';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface PageSection {
  slot: string;
}

/**
 * Returns true when the page identified by `alias` (or, as a fallback, by
 * `pathname`) has `HeroSolidBG` as its primary hero — i.e. an interior page
 * where the navigation overlays the solid-primary hero rather than a
 * photographic hero. Looking up by alias works during SSG (the runtime
 * passes `page.alias` through `usePage()`) so the resulting class is baked
 * into the static HTML and the nav text is correct on first paint.
 */
function isOverSolidHero(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData as Record<string, PageEntry>;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return hero?.slot === 'HeroSolidBG';
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state (Centre Point being the canonical
 * example — it has neither, but earlier versions of this check let it
 * leak into the nav when only one ID was populated as a placeholder).
 * If either is empty we drop the page from the nav (the SSG also marks
 * the page hidden + noindex so it isn't crawled — see ssg-entry.ts).
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    // URL has no defaultValue — the effective default is computed at render
    // time (`scheduleTourFallbackUrl`): `/schedule-a-tour` when the property
    // has a tour-scheduling provider configured, otherwise `/contact-us` so
    // the CTA doesn't drop visitors onto an empty scheduler. An explicit
    // editor override always wins.
    url: { label: 'URL', type: 'url' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const virtualTourLinkDef: SlotGroupDefinition = {
  label: 'Virtual Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tours' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 NOHO WAY ATLANTA, GA',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 12.07C22 6.48 17.52 2 11.93 2S1.86 6.48 1.86 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9v-2.91h2.4V9.84c0-2.37 1.41-3.68 3.57-3.68 1.03 0 2.12.18 2.12.18v2.33h-1.2c-1.18 0-1.55.73-1.55 1.48v1.78h2.64l-.42 2.91h-2.22V22c4.78-.75 8.44-4.91 8.44-9.93z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2zm-.2 2A3.6 3.6 0 004 7.6v8.8A3.6 3.6 0 007.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6A3.6 3.6 0 0016.4 4H7.6zm9.65 1.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z" />
    </svg>
  );
}

/** Match reference mockup: 123-456-7890 */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Nav path matching
// ============================================================================

/** Normalize a pathname for comparison — strip a single trailing slash
    except on the root path so `/floor-plans` and `/floor-plans/` match. */
function normalizePath(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

// ============================================================================
// Logo legibility probe (scrolled / white header)
// ----------------------------------------------------------------------------
// The scrolled header paints a solid WHITE background, so the mark it shows
// there has to read against white. We prefer the property's PRIMARY (full
// color) logo in that state, but a primary logo authored for dark backgrounds
// can be near-white and wash out. This best-effort probe loads a small CDN
// variant of the logo into a canvas and measures the alpha-weighted average
// luminance of its opaque ink; a value above LOGO_LIGHT_ON_WHITE_THRESHOLD
// means the ink is predominantly light and hard to see on white, so the caller
// falls back to the mono-black mark. The probe stays 'unknown' until it
// resolves and if the canvas is cross-origin tainted or the image fails to
// load — so an inconclusive result keeps the preferred primary mark.
// ============================================================================

const LOGO_LIGHT_ON_WHITE_THRESHOLD = 0.7;

type LogoLegibility = 'legible' | 'illegible' | 'unknown';

function useLogoLegibleOnWhite(
  logoUrl: string | null | undefined,
  enabled: boolean,
): LogoLegibility {
  const [state, setState] = useState<LogoLegibility>('unknown');

  useEffect(() => {
    if (!enabled || !logoUrl || typeof window === 'undefined') {
      setState('unknown');
      return;
    }
    let cancelled = false;
    const key = parseStorageKey(logoUrl);
    const src = key ? getMediaUrl(key, { width: 160 }) : logoUrl;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const w = Math.min(img.naturalWidth || 64, 96) || 64;
        const h = Math.min(img.naturalHeight || 64, 96) || 64;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          setState('unknown');
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let lumSum = 0;
        let alphaSum = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3] / 255;
          if (a <= 0.05) continue;
          const lum = relativeLuminance({ r: data[i], g: data[i + 1], b: data[i + 2] });
          lumSum += lum * a;
          alphaSum += a;
        }
        if (alphaSum <= 0) {
          setState('unknown');
          return;
        }
        const avgLum = lumSum / alphaSum;
        setState(avgLum > LOGO_LIGHT_ON_WHITE_THRESHOLD ? 'illegible' : 'legible');
      } catch {
        // Tainted canvas (cross-origin without CORS) or other failure — stay
        // inconclusive so the caller keeps the primary logo.
        setState('unknown');
      }
    };
    img.onerror = () => {
      if (!cancelled) setState('unknown');
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [logoUrl, enabled]);

  return state;
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  const overSolidHero = useMemo(
    () =>
      isOverSolidHero(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );

  // Pick the more readable text color for the transparent nav when it sits on
  // top of `HeroSolidBG` (which paints the section in `--vibe-color-primary`).
  // Uses the same `getContrastTextColor` helper the Footer uses (and that the
  // tokens runtime uses to compute `--vibe-color-text-on-primary`), so the
  // nav, the hero CTA, and the footer all pick the same contrast side on a
  // given primary.
  //
  // The earlier implementation here did `pickHighestContrast(primary,
  // [textOnPrimary, textOnSecondary])`, which was technically WCAG-optimal
  // but produced black text on medium-tone primaries (gold, tan, dusty
  // blue): on a primary with luminance ~0.3, dark-on-medium scores ~5:1 vs.
  // white-on-medium ~3:1, so the picker chose dark — disagreeing with the
  // runtime's own text-on-primary token and rendering muddy black nav links
  // on what should be a white-text design. The shared helper applies the
  // documented 0.55 threshold that favors white on those medium tones.
  const tokens = useTokens();
  const navOverHeroColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  // CTA accent color for the SCROLLED (white-background) nav state. The
  // outline-style `Schedule a Tour` button paints its border / label in this
  // color. When the property's primary is too pale to read against the white
  // header (contrast < WCAG AA 4.5:1), fall back to the dark `text` token so
  // the CTA stays legible after the transparent → solid header transition.
  // We also expose a paired "on-accent" color for the hover fill state — the
  // runtime keeps `textOnPrimary` calibrated to primary, so when we switch
  // the fill to `text` we have to pair it with `background` (white) instead.
  const [navCtaAccent, navCtaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode — e.g.
  // `custom_engrain_sightmap_url`) or given a literal string (Custom mode).
  // In both cases useContentToken returns the already-RESOLVED value (the
  // field's value, or the literal), so we simply expose the Interactive Map
  // page whenever that resolved value is non-empty. Blank by default (no
  // override). ADDITIVE to the ApartmentSync detection above, so existing
  // ApartmentSync-configured properties keep working untouched.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Drawer navigation prepends an explicit Home link (Home is excluded from
  // the top-bar nav builder, but the slide-in menu lists it first — matching
  // the reference design where the current page is highlighted).
  const drawerLinks = useMemo<NavLink[]>(
    () => [{ label: 'Home', href: '/' }, ...navLinks],
    [navLinks],
  );

  // Current pathname for the drawer's active-link highlight. Resolve from the
  // runtime page context first (works at SSG time) and fall back to the URL.
  const currentPath = normalizePath(
    pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : '/'),
  );
  const isActivePath = (href: string) => normalizePath(href) === currentPath;

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const primaryLogo = usePropertyField('property_primary_logo');
  const propertyName = usePropertyField('property_name');

  // Two logo variants for the two header states:
  //  - lightLogo: shown over the hero while the header is transparent — a
  //    white/mono-white mark reads against the dark hero photography.
  //  - darkLogo: shown once the header scrolls to its solid WHITE background.
  //    We prefer the PRIMARY (full color) mark here, and only fall back to the
  //    mono-black mark when the primary logo is detected as too light to read
  //    on white (see useLogoLegibleOnWhite). Both chains end at any available
  //    logo so a property that only has one mark still renders something rather
  //    than falling back to the wordmark text.
  const lightLogo = logoRaw.image || monoWhiteLogo || primaryLogo || monoBlackLogo;
  // Only worth probing the primary mark's legibility when there's a mono-black
  // mark to fall back to; otherwise the chain lands on the primary logo anyway.
  const primaryLegibility = useLogoLegibleOnWhite(
    primaryLogo,
    !!(primaryLogo && monoBlackLogo),
  );
  const darkLogo =
    (primaryLogo && primaryLegibility !== 'illegible' ? primaryLogo : null) ||
    monoBlackLogo ||
    primaryLogo ||
    logoRaw.image ||
    monoWhiteLogo;
  // Drawer sits on the white theme background, so prefer the dark mark; fall
  // back to the light mark, then to the wordmark text if neither exists.
  const drawerLogo = darkLogo || lightLogo;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  // Tour-scheduling availability. The default `multihub-tour-scheduler` block
  // is keyed by `custom_apartmentsync_javascript_id`; the alternate Elise /
  // RealPage tour blocks are keyed by their own per-property widget URLs. When
  // none of these are configured the `/schedule-a-tour` page renders an empty
  // scheduler (the block itself falls back to a "Contact Us" view), so we
  // point the Header CTA straight at `/contact-us` instead. An explicit editor
  // override on the slot group's URL still wins over this computed default.
  const tourProviderFields = ['custom_apartmentsync_javascript_id', 'custom_eliseai_tour_scheduler_url', 'custom_realpage_tour_widget'];
  const hasTourScheduling = tourProviderFields.some((field) => {
    const value = propertyForLeasing?.[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
  const scheduleTourFallbackUrl = hasTourScheduling ? '/schedule-a-tour' : '/contact-us';
  const virtualTourLink = useSlotGroup('header.virtual_tour_link', virtualTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const facebookLink = useSlotGroup('header.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('header.instagram_link', instagramLinkDef);

  // ─── Drawer details section (Visit Us / Office Hours / policies) ─────────
  const detailsVisitLabel = useContentToken('header.details_visit_label');
  const detailsHoursLabel = useContentToken('header.details_hours_label');
  const detailsPetPolicyLabel = useContentToken('header.details_pet_policy_label');
  const detailsQualifyingLabel = useContentToken('header.details_qualifying_label');
  const detailsApplicantFeesLabel = useContentToken('header.details_applicant_fees_label');
  const detailsOfficeHours = useCollectionBinding<OfficeHourEntry>('header.details_office_hours', {
    collection: 'office_hours',
  });
  const detailsHasHours = detailsOfficeHours.length > 0;

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when the slide-in menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close the slide-in menu on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out noho-header${scrolled ? ' header-scrolled noho-header--scrolled' : ' bg-transparent header-transparent'}${overSolidHero ? ' header-over-solid-hero' : ''}`}
        style={{
          top: 0,
          transition: 'background-color 0.5s ease',
          // The Special Offer Banner is a separate root-level fixed element at
          // z-index 60, while the slide-in drawer/scrim live inside this
          // header's own stacking context (z-50) and are therefore capped
          // below the banner. While the menu is open, lift the whole header
          // above the banner so the scrim dims it and the drawer covers it.
          ...(menuOpen ? { zIndex: 70 } : {}),
          // Main nav row picks up the white background via CSS when scrolled;
          // the utility bar stays dark at all times.
          ...(navOverHeroColor ? { ['--rise-nav-over-hero-text' as string]: navOverHeroColor } : {}),
          ['--rise-nav-cta-accent' as string]: navCtaAccent,
          ['--rise-nav-cta-on-accent' as string]: navCtaOnAccent,
        }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav (consumed by
            `hasInteractiveMapOverride` above — e.g. `custom_engrain_sightmap_url`).
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility bar — transparent strip with phone | address (wider) |
              virtual tours | resident access | apply now | schedule.
              Action links sit tighter on the right so address has room. */}
          {/* ============================================================ */}
          <div className="header-util-row noho-util-bar">
            <div className={`noho-util-grid${hasVirtualTours ? '' : ' noho-util-grid--no-virtual'}`}>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref noho-util-cell noho-util--phone"
                aria-label="Call us"
              >
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="noho-util-phone-text"
                >
                  {formatPhone(phone) || '123-456-7890'}
                </EditableToken>
              </a>

              <EditableSlotGroup
                groupPath="header.address_link"
                definition={addressLinkDef}
                className="noho-util-item noho-util--address"
              >
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="noho-util-cell"
                  aria-label="View on Google Maps"
                >
                  <span className="noho-util-cell-text">
                    {addressLink.address || '123 NOHO WAY ATLANTA, GA'}
                  </span>
                </a>
              </EditableSlotGroup>

              {hasVirtualTours && (
                <EditableSlotGroup
                  groupPath="header.virtual_tour_link"
                  definition={virtualTourLinkDef}
                  className="noho-util-item noho-util--virtual"
                >
                  <a
                    href={virtualTourLink.url || '/virtual-tour'}
                    target={virtualTourLink._target || '_self'}
                    className="noho-util-cell"
                  >
                    {virtualTourLink.label || 'Virtual Tours'}
                  </a>
                </EditableSlotGroup>
              )}

              <EditableSlotGroup
                groupPath="header.resident_access_link"
                definition={residentAccessLinkDef}
                className="noho-util-item noho-util--resident"
              >
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="noho-util-cell"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="header.apply_now_link"
                definition={applyNowLinkDef}
                className="noho-util-item noho-util--apply"
              >
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="noho-util-cell"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="header.schedule_tour_link"
                definition={scheduleTourLinkDef}
                className="noho-util-item noho-util--schedule"
              >
                <a
                  href={scheduleTourLink.url || scheduleTourFallbackUrl}
                  target={scheduleTourLink._target || '_self'}
                  className="noho-util-cell"
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Main bar — transparent strip. Desktop/tablet: logo left, socials +
              hamburger right. Mobile: phone icon left | centered logo |
              hamburger right (utility bar + socials hidden). */}
          <div className="noho-main-nav-row">
            <div className="noho-main-nav-inner">
              {/* Mobile-only quick-call icon (left of the centered logo). */}
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref noho-mobile-phone"
                aria-label="Call us"
              >
                <PhoneIcon className="w-6 h-6" />
              </a>

              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef} className="noho-logo-slot">
                <a href="/" className="noho-logo-link flex-shrink-0">
                  {lightLogo || darkLogo ? (
                    <>
                      {/* Light mark — visible while the header is transparent
                          over the hero. Hidden once the header scrolls white. */}
                      {lightLogo ? (
                        <VibesitesImage
                          url={lightLogo}
                          alt={logoRaw.alt || 'Community Logo'}
                          className="noho-logo-img noho-logo-img--light h-10 sm:h-12 lg:h-14 w-auto object-contain"
                          sizes="180px"
                        />
                      ) : null}
                      {/* Dark mark — swapped in when the header scrolls to its
                          solid white background so the logo stays legible. */}
                      {darkLogo ? (
                        <VibesitesImage
                          url={darkLogo}
                          alt={logoRaw.alt || 'Community Logo'}
                          className="noho-logo-img noho-logo-img--dark h-10 sm:h-12 lg:h-14 w-auto object-contain"
                          sizes="180px"
                        />
                      ) : null}
                    </>
                  ) : (
                    <span className="noho-logo-text">
                      {propertyName || 'NOHO'}
                    </span>
                  )}
                </a>
              </EditableSlotGroup>

              <div className="noho-nav-controls">
                <div className="noho-social-row">
                  <EditableSlotGroup groupPath="header.facebook_link" definition={facebookLinkDef}>
                    <a
                      href={facebookLink.url || '#'}
                      target={facebookLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="noho-social-icon"
                      aria-label="Facebook"
                      {...(!facebookLink.url ? { 'aria-disabled': true, onClick: (e: React.MouseEvent) => e.preventDefault() } : {})}
                    >
                      <FacebookIcon className="w-[14px] h-[14px]" />
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.instagram_link" definition={instagramLinkDef}>
                    <a
                      href={instagramLink.url || '#'}
                      target={instagramLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="noho-social-icon"
                      aria-label="Instagram"
                      {...(!instagramLink.url ? { 'aria-disabled': true, onClick: (e: React.MouseEvent) => e.preventDefault() } : {})}
                    >
                      <InstagramIcon className="w-[14px] h-[14px]" />
                    </a>
                  </EditableSlotGroup>
                </div>

                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="noho-hamburger"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-7 h-0.5 bg-white transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2.5'
                    }`}
                  />
                  <span
                    className={`absolute block w-7 h-0.5 bg-white transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-7 h-0.5 bg-white transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Slide-in navigation drawer (all breakpoints)                   */}
        {/* -------------------------------------------------------------- */}
        {/* Clicking the hamburger slides a panel in from the RIGHT over a  */}
        {/* dimming scrim. The scrim covers the whole viewport (including   */}
        {/* the header behind it, so the utility bar / logo read as         */}
        {/* dimmed) and closes the menu on click. Drawer is full-width on   */}
        {/* mobile and ~60% (capped) on tablet/desktop.                     */}
        {/* ============================================================== */}
        <div
          className={`noho-drawer-scrim${menuOpen ? ' is-open' : ''}`}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
        <div
          className={`noho-drawer${menuOpen ? ' is-open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          aria-hidden={!menuOpen}
        >
          <button
            type="button"
            className="noho-drawer-close"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          <div className="noho-drawer-inner">
            {/* Logo — dark mark on the white drawer, wordmark fallback. */}
            <a href="/" onClick={closeMobileMenu} className="noho-drawer-logo">
              {drawerLogo ? (
                <VibesitesImage
                  url={drawerLogo}
                  alt={logoRaw.alt || 'Community Logo'}
                  className="noho-drawer-logo-img"
                  sizes="200px"
                />
              ) : (
                <span className="noho-logo-text">{propertyName || 'NOHO'}</span>
              )}
            </a>

            {/* Primary CTAs. The utility bar (including Schedule a Tour)
                is hidden on mobile, so the tour link lives here with the
                other action buttons. */}
            <div className="noho-drawer-cta-row">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="noho-drawer-cta"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="noho-drawer-cta"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || scheduleTourFallbackUrl}
                  target={scheduleTourLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="noho-drawer-cta"
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Centered nav links with a divider dash between each; the
                current page is bold + dark. Home is listed first. */}
            <nav className="noho-drawer-nav" role="navigation" aria-label="Primary navigation">
              {drawerLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    onClick={closeMobileMenu}
                    className={`noho-drawer-link${isActivePath(link.href) ? ' is-active' : ''}`}
                    aria-current={isActivePath(link.href) ? 'page' : undefined}
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      onClick={closeMobileMenu}
                      className={`noho-drawer-link noho-drawer-link--child${isActivePath(child.href) ? ' is-active' : ''}`}
                      aria-current={isActivePath(child.href) ? 'page' : undefined}
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </nav>

            {/* Colored details section — sits directly below the nav list.
                Painted on the primary color, matching the site footer style:
                Visit Us + Office Hours, then Pet Policy / Qualifying Criteria /
                Applicant Fees. Full-bleed within the (white) drawer. */}
            <div
              className="noho-drawer-details"
              style={{
                backgroundColor: 'var(--vibe-color-primary)',
                color: 'var(--vibe-color-text-on-primary)',
              }}
            >
              <div className="ndd-top">
                <div className="ndd-col">
                  <EditableToken path="header.details_visit_label" as="h3" className="ndd-heading ndd-heading--center">
                    {detailsVisitLabel || 'Visit Us'}
                  </EditableToken>
                  <RichTextContent
                    path="header.details_visit_address"
                    as="div"
                    className="ndd-body ndd-body--center"
                  />
                </div>

                <div className="ndd-col">
                  <EditableToken path="header.details_hours_label" as="h3" className="ndd-heading ndd-heading--center">
                    {detailsHoursLabel || 'Office Hours'}
                  </EditableToken>
                  {detailsHasHours && (
                    <EditableCollectionBinding bindingPath="header.details_office_hours" label="Office Hours">
                      <div className="ndd-hours">
                        {condenseOfficeHours(detailsOfficeHours).map((row, i) => (
                          <div key={i} className="ndd-hours-row">
                            <span className="ndd-hours-day">{row.dayLabel}</span>
                            <span className="ndd-hours-time">{row.timeLabel}</span>
                          </div>
                        ))}
                      </div>
                    </EditableCollectionBinding>
                  )}
                </div>
              </div>

              <div className="ndd-policies">
                <div className="ndd-block">
                  <EditableToken path="header.details_pet_policy_label" as="h3" className="ndd-heading">
                    {detailsPetPolicyLabel || 'Pet Policy'}
                  </EditableToken>
                  <RichTextContent
                    path="header.details_pet_policy_description"
                    as="div"
                    className="ndd-body ndd-body--para"
                  />
                  <RichTextContent
                    path="header.details_pet_policy_details"
                    as="div"
                    className="ndd-body"
                  />
                </div>

                <div className="ndd-block">
                  <EditableToken path="header.details_qualifying_label" as="h3" className="ndd-heading">
                    {detailsQualifyingLabel || 'Qualifying Criteria'}
                  </EditableToken>
                  <RichTextContent
                    path="header.details_qualifying_criteria"
                    as="div"
                    className="ndd-body"
                  />
                </div>

                <div className="ndd-block">
                  <EditableToken path="header.details_applicant_fees_label" as="h3" className="ndd-heading">
                    {detailsApplicantFeesLabel || 'Applicant Fees'}
                  </EditableToken>
                  <RichTextContent
                    path="header.details_applicant_fees"
                    as="div"
                    className="ndd-body"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .rise-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Transparent header: utility links → white */
        .header-transparent .header-util-link {
          color: rgba(255,255,255,0.85);
        }
        .header-transparent .header-util-link:hover {
          color: #fff;
        }
        .header-transparent .header-util-divider {
          background: rgba(255,255,255,0.3);
        }

        /* Interior pages: utility links + divider use the picked on-color
           token instead of hardcoded white. We dim the base state via
           color-mix() to match the ~85% / 30% feel of the photographic
           variant above. */
        .header-transparent.header-over-solid-hero .header-util-link {
          color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 85%, transparent);
        }
        .header-transparent.header-over-solid-hero .header-util-link:hover {
          color: var(--rise-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .header-util-divider {
          background: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 30%, transparent);
        }

        /* Nav links: animated underline on hover (used by Header
           DesktopNavLink, "More" dropdown items, MobileNavLink, and the
           Navigation sub-bar). All of these contexts render against a
           white surface, so the affordance is an underline rather than a
           color shift — the underline is drawn via text-decoration so it
           rides the text baseline and works at any link padding. The
           transparent-header (over-hero) brightening hover above keeps
           its own color shift via earlier !important rules; the
           underline simply layers on top with currentColor. */
        .rise-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Address / phone rows in the drawer wear the rise-nav-darken-hover
           class on the inner text span (not the link itself, so the
           underline doesn't run under the icon). Trigger the underline from
           the parent link's :hover/:focus-visible so the whole row remains
           the hover target. */
        .noho-drawer .noho-drawer-contact-row:hover .rise-nav-darken-hover,
        .noho-drawer .noho-drawer-contact-row:focus-visible .rise-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row — sits above the main nav row inside .header-bar.   */
        /* ----------------------------------------------------------------- */
        /* Subtle divider line between the utility row and the main nav row
           when the header is in its scrolled (white-background) state. The
           transparent/over-hero variants below soften the divider to a
           translucent tint of the picked on-color so it still reads against
           dark hero photography or the solid primary section without
           painting a hard line. */
        .header-util-row {
          border-bottom: 1px solid transparent;
          transition: border-color 0.5s ease;
        }
        .header-scrolled .header-util-row {
          border-bottom-color: var(--vibe-color-border);
        }
        .header-transparent .header-util-row {
          border-bottom-color: rgba(255, 255, 255, 0.18);
        }
        .header-transparent.header-over-solid-hero .header-util-row {
          border-bottom-color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 18%, transparent);
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          /* Animated underline on hover — mirrors the .rise-nav-darken-hover
             treatment used by the main nav links so phone / address / apply
             now / resident access share the same hover language. The
             underline is drawn via text-decoration so it rides the text
             baseline and fades cleanly between transparent and currentColor.
             text-underline-offset is tighter (3px) than the nav links (6px →
             4px) because the utility row's row-height is smaller. */
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 5px;
          white-space: nowrap;
          transition: color 0.2s ease, text-decoration-color 0.25s ease, text-underline-offset 0.25s ease;
        }
        /* White-background hover matches the nav links' darken-25% rule.
           The .header-transparent and .header-over-solid-hero overrides
           below carry higher specificity so they still win when the
           header sits on top of a hero. */
        .header-util-link:hover {
          color: color-mix(in srgb, var(--vibe-color-primary), black 25%);
          text-decoration-color: currentColor;
          text-underline-offset: 3px;
        }
        .header-util-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: var(--vibe-color-border);
          flex-shrink: 0;
        }

        /* ================================================================ */
        /* CTA Button — transparent / outline style                        */
        /* ----------------------------------------------------------------- */
        /* Border + label use --rise-nav-cta-accent (luminance-picked).
           That variable resolves to --vibe-color-primary when the property's
           primary has WCAG AA contrast against the white scrolled header,
           and falls back to --vibe-color-text (the dark theme color) for
           pale primaries that would otherwise wash out on white. The hover
           fill follows the same accent so the swap is consistent. */
        .nav-cta-btn {
          background: transparent;
          border: 1.5px solid var(--rise-nav-cta-accent, var(--vibe-color-primary));
          border-radius: 2px;
          color: var(--rise-nav-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--rise-nav-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--rise-nav-cta-accent, var(--vibe-color-primary)) 25%, transparent);
          transform: translateY(-1px);
          color: var(--rise-nav-cta-on-accent, var(--vibe-color-text-on-primary));
        }
        .nav-cta-btn:hover::before {
          opacity: 1;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* When header is transparent (over hero) — white border & text */
        .header-transparent .nav-cta-btn {
          border-color: rgba(255,255,255,0.75);
          color: #fff;
        }
        .header-transparent .nav-cta-btn::before {
          background: rgba(255,255,255,0.18);
        }
        .header-transparent .nav-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          box-shadow: none;
        }

        /* Interior pages: CTA border / text follow the luminance-picked
           on-color token. The translucent fill that slides in on hover uses
           color-mix() so it stays in palette when the picked color is dark. */
        .header-transparent.header-over-solid-hero .nav-cta-btn {
          border-color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 75%, transparent);
          color: var(--rise-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn::before {
          background: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 18%, transparent);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn:hover {
          border-color: var(--rise-nav-over-hero-text, #fff);
          color: var(--rise-nav-over-hero-text, #fff);
          box-shadow: none;
        }

        /* ================================================================ */
        /* NOHO header — transparent over hero; solid dark bars on scroll  */
        /* ================================================================ */
        .noho-header .header-bar {
          border-bottom: none !important;
          box-shadow: none !important;
        }

        /* Top utility strip — transparent over hero; solidifies on scroll */
        .noho-util-bar {
          background: transparent !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.35) !important;
          overflow-x: auto;
          overflow-y: hidden;
        }
        .noho-header--scrolled .noho-util-bar,
        .header-scrolled .noho-util-bar {
          background: #ffffff !important;
          border-bottom-color: var(--vibe-color-border) !important;
        }

        /* Phone + address get more width; action links sit tighter on the right */
        .noho-util-grid {
          display: grid;
          grid-template-columns:
            minmax(0, 1.1fr)
            minmax(0, 2.2fr)
            minmax(0, 0.95fr)
            minmax(0, 0.95fr)
            minmax(0, 0.85fr)
            minmax(0, 1.05fr);
          width: 100%;
          min-width: 760px;
          min-height: 2.35rem;
          align-items: stretch;
        }
        /* Virtual Tours cell drops out when the property has no virtual tours;
           re-flow the remaining five cells (phone | address | resident |
           apply | schedule) so the bar has no empty column or dangling
           divider. */
        .noho-util-grid--no-virtual {
          grid-template-columns:
            minmax(0, 1.1fr)
            minmax(0, 2.2fr)
            minmax(0, 0.95fr)
            minmax(0, 0.85fr)
            minmax(0, 1.05fr);
          min-width: 680px;
        }
        .noho-util-grid > a.noho-util-cell,
        .noho-util-grid > .noho-util-item {
          display: flex;
          align-items: stretch;
          min-width: 0;
          height: 100%;
          border-right: 1px solid rgba(255, 255, 255, 0.5);
        }
        .noho-util-grid > .noho-util-item:last-child,
        .noho-util-grid > a.noho-util-cell:last-child {
          border-right: none;
        }

        /* Tablet: trim the utility bar to phone | resident | apply | schedule.
           Address and virtual tours drop out (still reachable from the drawer)
           so the remaining action links fill the bar without horizontal scroll.
           Schedule a Tour stays visible — hiding it left no in-header path to
           the tour CTA on this breakpoint. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .noho-util-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            min-width: 0;
          }
          .noho-util-grid > .noho-util--address,
          .noho-util-grid > .noho-util--virtual {
            display: none;
          }
        }
        /* Editable wrappers must fill the cell so phone text centers like peers */
        .noho-util-grid > a.noho-util-cell > *,
        .noho-util-grid > .noho-util-item > a.noho-util-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          min-width: 0;
        }
        .noho-util-cell {
          flex: 1 1 auto;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.4rem 0.5rem;
          font-family: var(--vibe-font-body), "Source Sans Pro", sans-serif;
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          color: #fff !important;
          text-decoration: none !important;
          text-align: center;
          white-space: nowrap;
          line-height: 1.2;
          transition: background 0.15s ease;
          min-width: 0;
          background: transparent;
          border: none;
          box-shadow: none;
        }
        .noho-util-cell:hover {
          color: #fff !important;
          background: rgba(255, 255, 255, 0.08);
          text-decoration: none !important;
        }
        .noho-util-cell-text {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        /* Kill legacy util-link underlines / muted colors on this bar */
        .noho-util-bar .header-util-link,
        .noho-util-bar a {
          text-decoration: none !important;
        }

        /* Main nav strip — transparent over hero; solidifies on scroll */
        .noho-main-nav-row {
          background: transparent !important;
          transition: background-color 0.4s ease;
        }
        .noho-header--scrolled .noho-main-nav-row {
          background: #ffffff !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        /* ---------------------------------------------------------------- */
        /* Scrolled (solid WHITE) header — flip every element that was tuned */
        /* for the dark/transparent state to a dark-on-white treatment so    */
        /* nothing washes out against the white background.                  */
        /* ---------------------------------------------------------------- */
        /* Utility row text + dividers */
        .header-scrolled .noho-util-cell {
          color: var(--vibe-color-text) !important;
        }
        .header-scrolled .noho-util-cell:hover {
          color: var(--vibe-color-primary) !important;
          background: rgba(0, 0, 0, 0.045);
        }
        .header-scrolled .noho-util-grid > a.noho-util-cell,
        .header-scrolled .noho-util-grid > .noho-util-item {
          border-right-color: var(--vibe-color-border);
        }
        /* Hamburger bars (hardcoded white in the markup) → dark ink */
        .header-scrolled .noho-hamburger span {
          background-color: var(--vibe-color-text) !important;
        }
        /* Social icons → dark ink, primary on hover */
        .header-scrolled .noho-social-icon {
          color: var(--vibe-color-text);
        }
        .header-scrolled .noho-social-icon:hover {
          color: var(--vibe-color-primary);
        }
        /* Logo swap: light mark over the hero, dark mark once scrolled white */
        .noho-logo-img--dark {
          display: none;
        }
        .header-scrolled .noho-logo-img--light {
          display: none;
        }
        .header-scrolled .noho-logo-img--dark {
          display: inline-block;
        }
        .noho-main-nav-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          min-height: 4.25rem;
          padding: 0.65rem 1.25rem 0.75rem;
        }
        @media (min-width: 1024px) {
          .noho-main-nav-inner {
            min-height: 4.75rem;
            padding: 0.75rem 1.75rem 0.85rem;
          }
        }

        /* Magenta outlined wordmark — matches reference (#802050 / #A04070).
           !important beats .header-transparent .font-heading { color:#fff }. */
        .noho-logo-text,
        .header-transparent .noho-logo-text,
        .header-scrolled .noho-logo-text,
        .noho-header .noho-logo-text {
          display: inline-block;
          font-family: var(--vibe-font-heading), "Lexend Deca", sans-serif;
          font-size: clamp(1.75rem, 3.5vw, 2.35rem);
          font-weight: 700;
          letter-spacing: 0.12em;
          line-height: 1;
          text-transform: uppercase;
          color: #802050 !important;
          -webkit-text-stroke: 1.75px #A04070;
          paint-order: stroke fill;
        }
        .noho-logo-img {
          max-height: 3.5rem;
        }

        /* Social icons beside hamburger (not stacked under it) */
        .noho-nav-controls {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 1rem;
          flex-shrink: 0;
        }
        .noho-hamburger {
          position: relative;
          width: 2.5rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .noho-social-row {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.85rem;
        }
        .noho-social-row > * {
          display: flex;
          align-items: center;
        }
        .noho-util-phone-text {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1.2;
        }
        .noho-social-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.92);
          transition: color 0.15s ease, opacity 0.15s ease;
        }
        .noho-social-icon:hover {
          color: #fff;
        }
        .noho-social-icon[aria-disabled="true"] {
          opacity: 0.4;
          cursor: default;
        }

        /* Mobile quick-call icon — hidden on tablet/desktop, shown on mobile. */
        .noho-mobile-phone {
          display: none;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.92);
          transition: color 0.15s ease;
        }
        .header-scrolled .noho-mobile-phone {
          color: var(--vibe-color-text);
        }

        /* ---------------------------------------------------------------- */
        /* Mobile header (< 768px) — single row to match the reference:      */
        /* phone icon (left) | centered logo | hamburger (right). The        */
        /* utility bar and the social icons drop out on this breakpoint.     */
        /* ---------------------------------------------------------------- */
        @media (max-width: 767px) {
          .noho-util-bar {
            display: none;
          }
          /* Taller bar + bigger touch targets so the mobile header fills the
             space rather than leaving a thin strip of small icons. */
          .noho-main-nav-inner {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            gap: 0.5rem;
            min-height: 5rem;
            padding: 0.85rem 1.25rem;
          }
          .noho-mobile-phone {
            display: inline-flex;
            justify-self: start;
          }
          /* Bump the quick-call icon up from w-6/h-6 to fill the taller bar. */
          .noho-mobile-phone svg {
            width: 1.85rem;
            height: 1.85rem;
          }
          .noho-logo-slot {
            justify-self: center;
          }
          /* Larger logo on mobile — the light/dark marks default to h-10
             (2.5rem via Tailwind); scale them up to better match the taller
             header. !important beats the equal-specificity .h-10 utility. */
          .noho-logo-img {
            height: 3.25rem !important;
            max-height: 3.25rem;
          }
          .noho-nav-controls {
            justify-self: end;
            gap: 0;
          }
          .noho-social-row {
            display: none;
          }
          /* Wider hamburger bars to visually balance the larger logo/phone. */
          .noho-hamburger span {
            width: 2rem !important;
          }
        }

        /* ================================================================ */
        /* Slide-in drawer + scrim                                          */
        /* ---------------------------------------------------------------- */
        /* The scrim sits above the fixed header (z-50) so the header reads  */
        /* as dimmed behind the open menu; the drawer sits above the scrim.  */
        /* ================================================================ */
        .noho-drawer-scrim {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.4s ease, visibility 0.4s ease;
          z-index: 60;
        }
        .noho-drawer-scrim.is-open {
          opacity: 1;
          visibility: visible;
        }

        .noho-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          background: var(--vibe-color-background);
          z-index: 62;
          transform: translateX(100%);
          visibility: hidden;
          transition: transform 0.42s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.42s ease;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          box-shadow: -8px 0 30px rgba(0, 0, 0, 0.18);
          display: flex;
          flex-direction: column;
        }
        .noho-drawer.is-open {
          transform: translateX(0);
          visibility: visible;
        }
        @media (min-width: 640px) {
          .noho-drawer {
            width: min(60%, 640px);
          }
        }
        /* Tablet: the details area splits into two columns (see
           .ndd-top at >=768px), which halves the width available to the
           Office Hours day/time rows and makes them wrap / feel cramped.
           Widen the drawer through the tablet range so both columns — and
           the hours rows in particular — get more breathing room. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .noho-drawer {
            width: min(80%, 720px);
          }
        }

        /* Close button — top-left of the drawer, rotates on hover. */
        .noho-drawer-close {
          position: absolute;
          top: 1rem;
          left: 1rem;
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: var(--vibe-color-text);
          cursor: pointer;
          padding: 0;
          z-index: 2;
          transition: color 0.2s ease, transform 0.3s ease;
        }
        .noho-drawer-close svg {
          width: 1.5rem;
          height: 1.5rem;
        }
        .noho-drawer-close:hover {
          color: var(--vibe-color-primary);
          transform: rotate(90deg);
        }

        .noho-drawer-inner {
          display: flex;
          flex-direction: column;
          min-height: 100%;
          padding-bottom: 1.5rem;
        }

        /* Logo */
        .noho-drawer-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3.5rem 1.5rem 1.75rem;
        }
        .noho-drawer-logo-img {
          height: 3.25rem;
          width: auto;
          max-width: 220px;
          object-fit: contain;
        }

        /* Primary CTA row: Apply Now, Resident Access, Schedule a Tour. */
        .noho-drawer-cta-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
          padding: 0 1.5rem 1.5rem;
        }
        .noho-drawer-cta-row > * {
          width: 100%;
        }
        .noho-drawer-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          text-align: center;
          padding: 0.95rem 0.75rem;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-body), "Source Sans Pro", sans-serif;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1.2;
          text-decoration: none;
          transition: background 0.2s ease;
        }
        .noho-drawer-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary), black 14%);
          color: var(--vibe-color-text-on-primary);
        }

        /* Centered nav links with a divider dash between each. */
        .noho-drawer-nav {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.75rem 1.5rem 0.5rem;
        }
        .noho-drawer-link {
          position: relative;
          display: block;
          width: 100%;
          padding: 0.9rem 0;
          font-family: var(--vibe-font-heading), "Lexend Deca", sans-serif;
          font-size: 1.5rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          line-height: 1.2;
          color: var(--vibe-color-primary);
          text-align: center;
          text-decoration: none;
          transition: color 0.2s ease, opacity 0.2s ease;
        }
        .noho-drawer-link:not(:last-child)::after {
          content: '';
          position: absolute;
          left: 50%;
          bottom: 0;
          transform: translateX(-50%);
          width: 28px;
          height: 1px;
          background: color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
        }
        .noho-drawer-link:hover {
          opacity: 0.65;
        }
        .noho-drawer-link.is-active {
          color: var(--vibe-color-text);
          font-weight: 700;
        }
        .noho-drawer-link--child {
          font-size: 1.15rem;
          font-weight: 400;
          opacity: 0.85;
        }

        /* Address + phone. */
        .noho-drawer-contact {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          margin-top: auto;
          padding: 1.5rem 1.5rem 0;
        }
        .noho-drawer-contact-row {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          font-family: var(--vibe-font-body), "Source Sans Pro", sans-serif;
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .noho-drawer-contact-row:hover {
          color: var(--vibe-color-primary);
        }

        /* ---------------------------------------------------------------- */
        /* Colored details section — sits directly below the drawer nav list */
        /* Full-bleed maroon block inside the white drawer, mirroring the    */
        /* site's property-details styling.                                  */
        /* ---------------------------------------------------------------- */
        .noho-drawer-details {
          margin-top: 1.5rem;
          padding: 2rem 1.5rem 2.25rem;
          text-align: left;
        }
        .noho-drawer-details .ndd-top {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
          align-items: start;
        }
        /* Side by side from tablet up; stacked on mobile only. */
        @media (min-width: 768px) {
          .noho-drawer-details .ndd-top {
            grid-template-columns: 1fr 1fr;
            gap: 1.25rem;
          }
        }
        .noho-drawer-details .ndd-heading {
          font-family: var(--vibe-font-heading), "Lexend Deca", sans-serif;
          font-size: 0.9rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin: 0 0 0.75rem;
          color: inherit;
        }
        .noho-drawer-details .ndd-heading--center {
          text-align: center;
        }
        .noho-drawer-details .ndd-body {
          font-family: var(--vibe-font-body), "Source Sans Pro", sans-serif;
          font-size: 0.9rem;
          line-height: 1.65;
          color: inherit;
          opacity: 0.92;
        }
        .noho-drawer-details .ndd-body--center {
          text-align: center;
        }
        .noho-drawer-details .ndd-body p {
          margin: 0 0 0.3rem;
        }
        .noho-drawer-details .ndd-body p:last-child {
          margin-bottom: 0;
        }
        .noho-drawer-details .ndd-body--para p {
          margin-bottom: 0.9rem;
        }
        .noho-drawer-details .ndd-body a {
          color: inherit;
          text-decoration: underline;
        }
        .noho-drawer-details .ndd-hours {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-width: 100%;
        }
        .noho-drawer-details .ndd-hours-row {
          flex-wrap: wrap;
        }
        .noho-drawer-details .ndd-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body), "Source Sans Pro", sans-serif;
          font-size: 0.9rem;
        }
        .noho-drawer-details .ndd-hours-day {
          font-weight: 500;
        }
        .noho-drawer-details .ndd-hours-time {
          opacity: 0.9;
          white-space: nowrap;
        }
        .noho-drawer-details .ndd-policies {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 2rem;
        }

        @media (prefers-reduced-motion: reduce) {
          .noho-drawer,
          .noho-drawer-scrim {
            transition: none;
          }
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
    'header.details_visit_label': {
      label: 'Menu Details — Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'header.details_visit_address': {
      label: 'Menu Details — Address',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_1"></span></p><p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'header.details_hours_label': {
      label: 'Menu Details — Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
    'header.details_pet_policy_label': {
      label: 'Menu Details — Pet Policy Label',
      type: 'text',
      defaultValue: 'Pet Policy',
    },
    'header.details_pet_policy_description': {
      label: 'Menu Details — Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'header.details_pet_policy_details': {
      label: 'Menu Details — Pet Policy Details',
      type: 'richtext',
      defaultValue:
        '<p>One-Time Pet Fee: <span data-type="field-token" data-field="property_upfront_pet_fee"></span></p><p>Pet Limit: <span data-type="field-token" data-field="property_max_pets"></span></p><p>Weight Limit: <span data-type="field-token" data-field="property_max_pet_weight_limit"></span> lbs</p><p>Restricted Breeds: <span data-type="field-token" data-field="property_pet_restrictions"></span></p>',
    },
    'header.details_qualifying_label': {
      label: 'Menu Details — Qualifying Criteria Label',
      type: 'text',
      defaultValue: 'Qualifying Criteria',
    },
    'header.details_qualifying_criteria': {
      label: 'Menu Details — Qualifying Criteria',
      type: 'richtext',
      defaultValue:
        '<p>Touring: Government issued I.D is required to tour.</p><p>Income: Income verification is required during the application process.</p><p>Credit/Criminal: Unsatisfactory credit may require additional deposit &amp; criminal background checks are required.</p>',
    },
    'header.details_applicant_fees_label': {
      label: 'Menu Details — Applicant Fees Label',
      type: 'text',
      defaultValue: 'Applicant Fees',
    },
    'header.details_applicant_fees': {
      label: 'Menu Details — Applicant Fees',
      type: 'richtext',
      defaultValue:
        '<p>Admin Fee: <span data-type="field-token" data-field="property_admin_fee"></span></p><p>Application Fee: <span data-type="field-token" data-field="property_application_fee"></span> per applicant</p>',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        // URL has no defaultValue — the effective default is computed at
        // render time: `/schedule-a-tour` when a tour-scheduling provider is
        // configured, otherwise `/contact-us`. Editor overrides still win.
        url: { label: 'URL', type: 'url' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.virtual_tour_link': {
      label: 'Virtual Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tours' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 NOHO WAY ATLANTA, GA',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.facebook_link': {
      label: 'Facebook Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_facebook_url',
          suggestedFields: ['property_facebook_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.instagram_link': {
      label: 'Instagram Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_instagram_url',
          suggestedFields: ['property_instagram_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.details_office_hours': {
      label: 'Menu Details — Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
